<script setup lang="ts">
  /**
   * Save bar — 03-components/23-sticky-bar · OSCloud DS 1.1.34
   */
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import type { SaveStatusState } from '@/composable/useSaveStatus'

  const props = withDefaults(
    defineProps<{
      state?: SaveStatusState
      dirtyText?: string
      savedText?: string
      ariaLabel?: string
    }>(),
    {
      state: 'idle',
      dirtyText: 'You have unsaved changes.',
      savedText: 'All changes saved.',
      ariaLabel: 'Actions',
    },
  )

  // `idle` deliberately has no copy: an untouched form has nothing to report.
  const statusText = computed(() => {
    if (props.state === 'dirty') return props.dirtyText
    if (props.state === 'saved') return props.savedText
    return ''
  })

  // The bar docks against the bottom edge of the scrollport. Once the end of the
  // form scrolls into view it settles into the flow instead, and should look like
  // a normal card again — so track which of the two it currently is. The 1px
  // bottom inset makes a docked bar (flush with the edge) fall short of a full
  // intersection, while a settled one sits clear of it.
  const root = ref<HTMLElement | null>(null)
  const isDocked = ref(false)
  let observer: IntersectionObserver | null = null

  onMounted(() => {
    if (!root.value || typeof IntersectionObserver === 'undefined') return
    observer = new IntersectionObserver(
      ([entry]) => {
        isDocked.value = entry.intersectionRatio < 1
      },
      { threshold: [1], rootMargin: '0px 0px -1px 0px' },
    )
    observer.observe(root.value)
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
  })
</script>

<template>
  <!-- State is read off data-state / data-docked, never a modifier class. `idle`
       carries no data-state at all, matching the card. -->
  <div
    ref="root"
    class="save-bar"
    :data-state="state === 'idle' ? undefined : state"
    :data-docked="isDocked || undefined"
    :aria-label="ariaLabel"
  >
    <div class="save-bar__info">
      <slot name="status">
        <!-- Always rendered, empty in `idle`: a live region has to be in the DOM
             before its content changes for the update to be announced. -->
        <span class="save-bar__status" role="status" aria-live="polite">
          <template v-if="statusText">
            <span class="save-bar__dot" aria-hidden="true" />
            {{ statusText }}
          </template>
        </span>
      </slot>
    </div>
    <div class="save-bar__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .save-bar {
    position: sticky;
    z-index: 9;
    // Sticky offsets start at the scrollport's padding edge, so cancel the
    // layout's content padding to reach the actual bottom of the viewport.
    bottom: calc(-1 * var(--bf-content-pad-bottom, 0px));
    margin-top: 24px;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 66px;
    background: var(--bf-white);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    box-shadow: var(--bf-shadow-1);
    transition:
      box-shadow var(--bf-dur-base) var(--bf-ease),
      border-color var(--bf-dur-base) var(--bf-ease),
      border-radius var(--bf-dur-base) var(--bf-ease);

    // Flush against the bottom edge: square off the corners that meet it and
    // lift the bar off the content with a stronger shadow.
    &[data-docked] {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      box-shadow: var(--bf-shadow-2);
    }

    &[data-state='dirty'] {
      border-top-color: var(--bf-orange-dark);
      box-shadow: var(--bf-shadow-2);
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      font-family: var(--bf-font-display);
      font-size: 13px;
      font-weight: var(--bf-fw-medium);
      color: var(--bf-deep-blue);
      transition: color var(--bf-dur-base) var(--bf-ease);
    }

    // The confirmation reads as a whole, so the copy carries the same green as
    // its dot rather than leaving the text neutral.
    &[data-state='saved'] &__status {
      color: var(--bf-success);
    }

    &__dot {
      width: 7px;
      height: 7px;
      flex-shrink: 0;
      border-radius: var(--bf-r-pill);
      background: var(--bf-dusky-2);
      transition:
        background var(--bf-dur-base) var(--bf-ease),
        box-shadow var(--bf-dur-base) var(--bf-ease);
    }

    &[data-state='dirty'] &__dot {
      background: var(--bf-orange-dark);
      box-shadow: 0 0 0 3px rgba(234, 96, 10, 0.18);
    }

    &[data-state='saved'] &__dot {
      background: var(--bf-success);
      box-shadow: 0 0 0 3px rgba(1, 179, 87, 0.18);
    }

    &__actions {
      display: flex;
      gap: 10px;
    }
  }

  @media (max-width: 640px) {
    .save-bar {
      flex-direction: column;
      align-items: stretch;
    }
  }
</style>
